/* eslint-disable */
import {
	Autocomplete,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import { SelectPicker } from 'rsuite';
import { Filter } from 'lucide-react';
import moment from 'moment';
import { analyticsAction, formActions } from '../../Actions';
import styles from './ImsStockFilter.module.scss';
import { authHeader, classes, convertToHigherUnit, filtersInfoChip, handleExcelDownload, skuTypesList } from '../../Helpers';
import ImagesFinder from '../ImagesFinder/ImagesFinder';
import drawerActions from '@/Actions/drawer.actions';
import { Button } from '../ui/button';
import GlobalSearch from '../globalSearch/search';
import { getCurrentPageNavInfo } from '@/Helpers';
import assetServices from '../../Services/AssetServices/asset.services';

const exportInfo = {
	showExportBtn: true,
};

function ImsStockFilter(props) {
	const {
		stockDeficit,
		setStockDeficit,
		type,
		filters,
		setFilters,
		resetInventoryAnalyticsState,
		data,
		setSearch,
		navState,
		assetId,
		setAssetId,
		setAssetName,
	} = props;

	const dispatch = useDispatch();
	const [assetsList, setAssetsList] = useState([]);
	const [assetsLoading, setAssetsLoading] = useState(false);

	const handleAssetChange = (value) => {
		setAssetId(value);
		const asset = assetsList.find((asset) => asset.value === value);
		setAssetName(asset?.label);
	};

	// Fetch assets list from API
	const fetchAssetsList = async (storeId) => {
		if (!storeId) return;
		setAssetsLoading(true);
		try {
			const result = await assetServices.assetProductInventoryService({
				req: { data: { storeId, type: 'Display Freezer' } },
			});
			if (result?.status === 'Success' && result?.data?.assets) {
				const formattedAssets = result.data.assets.map((asset) => ({
					value: asset.id,
					label: asset.name,
				}));
				setAssetsList(formattedAssets);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching assets:', error);
		} finally {
			setAssetsLoading(false);
		}
	};

	// Fetch assets when store changes
	useEffect(() => {
		if (filters?.store?.id && type !== 'PROCESS_REPORT') {
			setAssetId(null);
			fetchAssetsList(filters.store.id);
		}
	}, [filters?.store?.id, type]);

	useEffect(() => {
		dispatch(drawerActions.toggleFiltersDrawer(true));
		resetInventoryAnalyticsState({
			entity: 'INVENTORY_ANALYTICS',
			method: 'SKU_STOCK',
			data: {},
		});
	}, [type]);

	const fetchStockReport = (filterValue) => {
		if (type === 'PROCESS_REPORT') {
			if (filterValue?.processTypes?.id?.length === 0 || !filterValue?.processTypes) return;
		} else if (!filterValue?.store?.id || !filterValue?.tab) return;

		setFilters(filterValue);
		const req = {};
		if (type === 'PROCESS_REPORT') {
			req.params = {
				type: filterValue?.tab,
				storeId: filterValue?.store?.id,
				processId: filterValue?.processTypes?.id,
			};
		} else {
			req.params = {
				type: filterValue?.tab,
				storeId: filterValue?.store?.id,
				...(assetId && assetsList?.length > 0 && assetsList?.find((asset) => asset.value === assetId) && { assetId }),
			};
		}

		dispatch(analyticsAction.gateway({
			entity: 'INVENTORY_ANALYTICS',
			req,
			method: 'SKU_STOCK',
		}));
	};

	useEffect(() => {
		if (filters?.store?.id && filters?.tab && type !== 'PROCESS_REPORT') {
			fetchStockReport({ ...filters, assetId });
		}
	}, [assetId]);

	const handleExport = () => {
		const currDate = moment(new Date()).format('YYYY-MM-DD hh:mm');
		const headers = [
			'UY Variant ID',
			'SKU Name',
			'SKU Type',
			'SKU Code',
			'Category',
			'Sub Category',
			'Unit',
			'Item Rate',
			'Current Stock',
			'Stock worth',
			'Threshold',
			'Max Stock',
		];
		const totalValues = [];
		const filterData = (Array.isArray(data) ? data : Array?.from(data))?.filter((item) => {
			if (filters?.skuType === 'ALL' || !filters?.skuType) return true;
			return item?.skuType?.toLowerCase().includes(filters?.skuType?.toLowerCase());
		});
		filterData?.forEach((item) => {
			const skuName = item?.skuName?.split(',').join("'");
			const perUnit = convertToHigherUnit(item?.unit)?.unit;
			item?.currentStock?.forEach((stock, idx) => {
				const productName =
					skuName +
					(stock.variantName ? ` (${stock.variantName})` : '');
				// stock?.rate ? stock?.rate + ' per ' + perUnit : '-'
				const rate = stock?.rate ? stock?.rate : '-';
				const maxStock = item?.maxStock?.find((ele) => {
					return Number(ele?.variantId) === Number(stock?.variantId);
				})?.qty?.split(' ')[0] ?? '-';
				const threshold = item?.threshold?.find((ele) => {
					return Number(ele?.variantId) === Number(stock?.variantId);
				})?.qty?.split(' ')[0] ?? '-';
				totalValues?.push([
					`${stock?.variantId ?? '-'}`,
					`${productName}`,
					`${item?.skuType ?? '-'}`,
					`${stock?.clientVariantId ?? '-'}`,
					`${item?.categoryName ?? '-'}`,
					`${item?.subCategoryName ?? '-'}`,
					`${perUnit}`,
					`${rate}`,
					`${stock.qty.split(' ')[0] ?? '-'}`,
					`${stock?.stockAmount?.toFixed(2) ?? '-'}`,
					`${threshold}`,
					`${maxStock}`,
				]);
			});
		});
		handleExcelDownload(headers, totalValues, `Current Stock ${currDate}`);
	};

	const filtersInfo = {
		header: {
			store: false,
			product: false,
			process: type !== 'PROCESS_REPORT',
			statusAndTypes: false,
		},
		store: {
			show: true,
			permittedStore: true,
			text: 'Store',
		},
		...(type === 'PROCESS_REPORT' ? {
			processType: {
				show: true,
				text: 'process',
			},
		} : {}),
		skuType: {
			show: true,
			text: 'SKU Type',
		},
		tab: {
			show: true,
			list: [
				{
					id: 'stock',
					type: 'Stocked',
				},
				{
					id: 'rejected',
					type: 'Rejected',
				},
			],
		},
	};

	return (
		<div className={styles.root}>
			<ImagesFinder
				filtersInfo={filtersInfo}
				onSubmit={fetchStockReport}
				exportInfo={exportInfo}
				allStoreOption={false}
			/>
			<p className='text-lg font-bold m-1'>{getCurrentPageNavInfo(navState)?.subMenu?.name}</p>
			<div className='flex flex-1 items-baseline gap-3'>
				<div className='flex-1 min-w-0'>
					<GlobalSearch
						placeholder='Search By Name'
						searchDropDown={false}
						setSearchValue={setSearch}
					/>
				</div>
				{type !== 'PROCESS_REPORT' && assetsList?.length > 0 && (
					<SelectPicker
						data={assetsList}
						value={assetId}
						onChange={(value) => handleAssetChange(value)}
						placeholder='Select Asset'
						searchable
						cleanable
						loading={assetsLoading}
						style={{ width: 200 }}
						disabled={!filters?.store?.id}
					/>
				)}
				{type !== 'PROCESS_REPORT' && (
					<button
						onClick={() => setStockDeficit(!stockDeficit)}
						className={`px-5 py-2 text-xs font-bold rounded transition-all ${stockDeficit
							? 'bg-slate-700 text-white'
							: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
							}
              `}
					>
						DEFICIT
					</button>
				)}
				{type !== 'PROCESS_REPORT' && (
					<div className='flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full'>
						<span className='w-3 h-3 bg-red-500 rounded-full' />
						<span className='text-xs text-gray-700 whitespace-nowrap'>
							Current Stock &lt; Threshold
						</span>
					</div>
				)}
				<Button
					onClick={() => {
						dispatch(drawerActions.toggleFiltersDrawer(true, {
							exportFrontend: true,
							handleExportFrontend: handleExport,
						}));
					}}
					className='flex items-center gap-2 px-4 py-2 text-white bg-teal-600 hover:bg-teal-700 rounded text-sm font-medium transition-colors'
				>
					<span className='text-nowrap'>Filter & Export</span>
					<Filter className='size-4' />
				</Button>
			</div>
			{filtersInfoChip(filtersInfo, filters)}
		</div>
	);
}

const mapStateToProps = (state) => {
	const data = state.analytics.INVENTORY_ANALYTICS.SKU_STOCK;
	const { navState } = state.navigation;
	return {
		data,
		navState,
	};
};

const mapDispatchToProps = {
	resetInventoryAnalyticsState: analyticsAction.resetInventoryAnalyticsState,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImsStockFilter);
